import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, FormControlLabel, InputBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { i18n } from '../../../../../../language';
import { ArrowLeftIcon, AssignIcon, CheckListIcon, DeleteIcon, TagIcon } from '../../../../../../components/Icon';
import { WarpSwitch } from '../../../../../../components/WarpSwitch';
import lengthVerification, { remainingLength } from '../../../../../../utils/lengthVerification';
import { AutoCompleteInput } from '../../../../../../components';
import { getArrayRandomKey, onKeyUpEnterOrSpace } from '../../../../../../utils';
import Tag from '../../../CreateNote/tag';
import { ehandoverAPI } from '../../../../../../apis';
import { WarnAlert } from '../../../CreateNote';
import { getValBasedOnChecklistDisplay } from '../../../../../../utils/eHandoverUtil';
import CheckList from './CheckList';
import Notify from './Notify';
import SendTo from './SendTo';

const useStyles = makeStyles((theme) => ({
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.714rem',
    color: '#001d63',
    fontWeight: 'bold',
    '& .MuiInputBase-input': {
      padding: '0',
      height: '3.5rem',
    },
    backgroundColor: '#faf3a2'
  },
  titleBox: {
    display: 'flex',
    marginBottom: '0.313rem',
  },
  titleIconBox: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    fontSize: '1.563rem',
    margin: '0.2rem 0 0 1rem',
    color: theme.palette.font.main,
    cursor: 'pointer',
  },
  topBox: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    '& .MuiButton-root': {
      color: theme.palette.font.sub,
      fontFamily: 'NotoSans-Medium',
      fontWeight: '500',
    },
    '& .MuiButton-root.MuiButton-textPrimary': {
      color: theme.palette.primary.main,
    },
  },
  delBtn: {
    textTransform: 'inherit',
  },
  arrowLeftIcon: {
    fontSize: '1rem',
    color: '#538fff',
  },
  back: {
    paddingLeft: '1rem',
    fontSize: '1rem',
    color: '#538fff',
    fontFamily: 'NotoSans-Bold',
  },
  templateContainer: { borderRadius: '0.857rem', width: '100%', flex: 1, backgroundColor: '#FFFFFF' },
  noteBox: {
    padding: '2.142rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.428rem',
  },
  acknowledgeBox: {
    margin: '0 0 0.5rem 0',
    width: 'fit-content',

    '& .MuiFormControlLabel-label': {
      marginLeft: '0.571rem',
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      fontWeight: '500',
      fontSize: '1.142rem',
    },
  },
  input: {
    fontFamily: 'NotoSans-Medium',
    border: '0',
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    width: '100%',
    fontWeight: '500',
    letterSpacing: '0',
    color: theme.palette.font.main,
    height: '100%',
  },
  inputTip: {
    fontFamily: 'NotoSans-Medium',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    margin: '0 0 -0.0625rem',
    borderBottom: '0.0625rem solid #ddd',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  checkBox: {
    '& .MuiSvgIcon-root': {
      height: '2rem',
      width: '2rem',
    },
    '&.MuiCheckbox-colorSecondary.Mui-checked': {
      color: theme.palette.status.green,
      borderRadius: '0.315rem',
    },
    '&.MuiCheckbox-root': {
      color: '#c0c6cb',
    },
  },
  tagsBox: { display: 'flex', gridGap: '0.5rem', margin: '0 1.875rem', flexWrap: 'wrap' },
  tags: {
    display: 'flex',
    // flexWrap: 'wrap',
    alignItems: 'center',
    gridGap: '0.5rem',
    flex: 1,
  },
  tagIcon: {
    fontSize: '1.375rem',
    color: theme.palette.font.sub,
  },
  groupBox: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'NotoSans-Medium',
    color: '#2E4469',
    gridGap: '0.5rem',
  },
  inputBox: {
    padding: '0.642rem 0.857rem',
    borderRadius: '0.714rem',
    border: '1px solid #E4E7EB',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: '1rem',
    cursor: 'pointer',
    width: '25rem',
  },
  groupName: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#2E4469',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  editBtn: {
    fontSize: '1.142rem',
    fontWeight: 500,
    color: '#538FFF',
  },
  noteFooter: {
    borderTop: '0.0625rem solid #ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gridGap: '1.25rem',
    padding: '1.142rem 0',
  },
  cancelBtn: {
    textTransform: 'capitalize',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    backgroundColor: '#eff6ff',
    color: '#538fff',
    fontWeight: '500',
    borderRadius: '0.625rem',
    border: '0.12rem solid #538fff',
    width: '12.5rem',
    height: '3.42rem',
    '&:hover': {
      backgroundColor: '#eff6ff',
    },
    '&:disabled': {
      backgroundColor: '#c0c6cb !important',
      color: '#ffffff !important',
    },
  },
  confirmBtn: {
    textTransform: 'capitalize',
    width: '12.5rem',
    height: '3.42rem',
    borderRadius: '0.5rem',
    backgroundColor: '#4c93ff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    color: '#FFFFFF',
    fontWeight: '500',
    '&:hover': {
      backgroundColor: '#4c93ff',
    },
    '&:disabled': {
      backgroundColor: '#c0c6cb !important',
      color: '#ffffff !important',
    },
  },
  grid: {
    width: '100%',
    height: '3.813rem',
    borderTop: '0.0625rem solid #e4e7eb',
    overflow: 'hidden',
    borderRadius: '0 0 0.9375rem 0.9375rem',
    display: 'flex',
    '& $paper:last-child': {
      borderRight: 'none',
    },
  },
  paper: {
    textAlign: 'center',
    height: '100%',
    borderRight: '0.0625rem #e4e7eb solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  icon: {
    fontSize: '1.5rem',
    marginRight: '0.75rem',
    color: '#5f9df6',
  },
  text: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.25rem',
    lineHeight: '1.875rem',
    color: theme.palette.font.main,
  },
}));

export default function EditTemplate({
  templateInfo,
  setEditTemplate,
  individualUserOptions,
  tagOptionList,
  initCheckListItem,
  getTaskTemplateList,
}) {
  const classes = useStyles();
  const [tagId, setTagId] = useState(null);
  const titleRef = useRef(null);
  const showTagsBox = useMemo(
    () => _.isArray(templateInfo?.tags) && templateInfo?.tags.length > 0,
    [templateInfo?.tags]
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const [showCheckListBox, setShowCheckListBox] = useState(
    _.isArray(templateInfo?.checkLists) && templateInfo?.checkLists.length > 0
  );
  const descriptionInputRef = useRef(null);

  const handleChangeTemplate = (name, val) => {
    setEditTemplate((prev) => ({ ...prev, [name]: val }));
  };
  const handleAddTag = (tagId) => {
    if (!tagId || !_.isNumber(tagId)) return;

    const currentTag = _.find(tagOptionList, (item) => item?.value === tagId);
    const newTagList = [...(templateInfo?.tags??[]), currentTag];
    handleChangeTemplate('tags', newTagList);
  };
  const handleDeleteTag = (index) => {
    const newTagList = [...(templateInfo?.tags??[])];
    newTagList.splice(index, 1);
    handleChangeTemplate('tags', newTagList);
  };
  const handleCancel = () => {
    setEditTemplate(null);
  };
  const isNull = (str) => {
    if (str === '') return true;
    let re = /^ +$/;
    return re.test(str);
  };
  const handleSave = async () => {
    if (isNull(templateInfo?.description)) {
      setAlertOpen(true);
      descriptionInputRef.current.focus();
      return;
    }

    const sendToRoomTypes = templateInfo?.sendToList?.roomTypeList;
    const sendToStaffs = templateInfo?.sendToList?.staffList;
    const notifyRoomTypes = templateInfo?.notifyList?.roomTypeList;
    const notifyStaffs = templateInfo?.notifyList?.staffList;
    const newCheckLists = templateInfo?.checkLists.filter((item) => item.title !== '');
    const checklistCorpList = _.map(notifyStaffs, (item) => ({
      corpId: item?.corpId,
      staffName: item?.staffName,
    }));
    const checklistRoomTypes = _.map(notifyRoomTypes, (item) => item?.value);

    let params = {
      templateName: templateInfo?.templateName,
      mark: templateInfo?.requiredAcknowLedge ? 'N' : null,
      title: templateInfo?.description,
      description: templateInfo?.description,
      checkLists: getValBasedOnChecklistDisplay(showCheckListBox, [], newCheckLists),
      checklistComplete: getValBasedOnChecklistDisplay(
        showCheckListBox,
        'N',
        templateInfo?.checklistComplete ? 'Y' : 'N'
      ),
      itemComplete: getValBasedOnChecklistDisplay(showCheckListBox, 'N', templateInfo?.itemComplete ? 'Y' : 'N'),
      checklistConfig: {
        checklistCorpList: getValBasedOnChecklistDisplay(showCheckListBox, [], checklistCorpList),
        checklistRoomTypes: getValBasedOnChecklistDisplay(showCheckListBox, [], checklistRoomTypes),
        isBasedSendTo: getValBasedOnChecklistDisplay(
          showCheckListBox,
          'Y',
          templateInfo?.notifyBaseOnSendTo ? 'Y' : 'N'
        ),
      },
      corpList: _.map(sendToStaffs, (item) => ({ corpId: item?.corpId, staffName: item?.staffName })),
      roomType: _.map(sendToRoomTypes, (item) => item?.value),
      tags: _.map(templateInfo?.tags, (item) => {
        return item.id;
      }),
    };
    try {
      if (templateInfo?.templateId) {
        params.templateId = templateInfo?.templateId;
        await ehandoverAPI.updateTaskTemplate(params);
      } else {
        await ehandoverAPI.addTaskTemplate(params);
      }
      await getTaskTemplateList(individualUserOptions);
      handleCancel();
    } catch (error) {}
  };
  const handleDelTemplate = async () => {
    try {
      await ehandoverAPI.deleteTaskTemplate({ templateId: templateInfo?.templateId });
      await getTaskTemplateList(individualUserOptions);
      handleCancel();
    } catch (error) {}
  };

  const disableSaveBtn = (templateInfo?.templateName ?? '') === '' ;

  return (
    <Box display="flex" flexDirection="column" gridGap="1.428rem" alignItems="flex-start" height="100%">
      {/* Template Title */}
      <Box className={classes.topBox}>
        <Box className={classes.titleBox}>
          <InputBase
            ref={titleRef}
            className={classes.title}
            placeholder='Fill the template name here'
            value={templateInfo?.templateName}
            required 
            onChange={(event) => {
              handleChangeTemplate('templateName', event.target.value);
            }}
            id="ehandover-note-template-templateName"
          />
          <div
            className={classes.titleIconBox}
            onClick={() => {
              titleRef.current.querySelector('input').focus();
            }}
            id="DutyAssign-div-agp"
            tabIndex={0}
            onKeyUp={(e) =>
              onKeyUpEnterOrSpace(e, () => {
                titleRef.current.querySelector('input').focus();
              })
            }
          >
            <AssignIcon className={classes.titleIcon} />
          </div>
        </Box>
        {templateInfo?.id && (
          <Button
            id="ehandover-note-template-del-template-btn"
            className={classes.delBtn}
            startIcon={<DeleteIcon />}
            onClick={handleDelTemplate}
          >
            {i18n.t('EHANDOVER_DELETE_TEMPLATE')}
          </Button>
        )}
      </Box>

      <ButtonBase onClick={() => setEditTemplate(null)}>
        <ArrowLeftIcon className={classes.arrowLeftIcon} />
        <div className={classes.back}>{i18n.t('BEDSTATUS_BACK')}</div>
      </ButtonBase>
      <Box className={classes.templateContainer}>
        <Box className={classes.noteBox}>
          {/* Require Acknowledge */}
          <FormControlLabel
            control={
              <WarpSwitch
                checked={templateInfo?.requiredAcknowLedge}
                onChange={(event) => handleChangeTemplate('requiredAcknowLedge', event.target.checked)}
                color="primary"
              />
            }
            label={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGEMENT')}
            className={classes.acknowledgeBox}
          />

          {/* Note Msg */}
          <Box>
            <InputBase
              id="ehandover-note-template-text"
              className={classes.input}
              placeholder={i18n.t('EHANDOVER_CREATENOTE_INPUT')}
              multiline
              variant="outlined"
              value={templateInfo?.description}
              inputRef={descriptionInputRef}
              onChange={(event) => {
                if (lengthVerification(event.target.value, 3001)) {
                  return;
                }
                handleChangeTemplate('description', event.target.value);
              }}
            />
            <div className={classes.inputTip}>
              {i18n.t('EHANDOVER_CREATENOTE_INPUT_REMAINING', {
                value: remainingLength(templateInfo?.description ?? '', 3000),
              })}
            </div>
          </Box>

          {showCheckListBox && (
            <>
              {/* Check list */}
              <CheckList
                handleChangeTemplate={handleChangeTemplate}
                checkList={templateInfo?.checkLists}
                initCheckListItem={initCheckListItem}
              />

              {/* Notify */}
              <Notify
                templateInfo={templateInfo}
                handleChangeTemplate={handleChangeTemplate}
                individualUserOptions={individualUserOptions}
              />
            </>
          )}

          {/* Tags && Send To */}
          <Box display="flex" alignItems="flex-start" gridGap="1rem" flexWrap="wrap">
            <div className={classes.tags}>
              <TagIcon className={classes.tagIcon} />
              <AutoCompleteInput
                id="ehandover-note-template-tags-select"
                width="15.6rem"
                height="2.5rem"
                placeholder={i18n.t('EHANDOVER_CREATENOTE_TAG')}
                itemList={tagOptionList}
                value={tagId}
                onChange={(id) => {
                  setTagId(id);
                  if (!_.some(templateInfo?.tags, (item) => item?.tagId === id)) {
                    handleAddTag(id);
                  }
                }}
              />
            </div>

            <SendTo
              sendToList={templateInfo?.sendToList}
              handleChangeTemplate={handleChangeTemplate}
              individualUserOptions={individualUserOptions}
              notifyBaseOnSendTo={templateInfo?.notifyBaseOnSendTo}
            />
          </Box>

          {/* Tags  */}
          {showTagsBox && (
            <Box className={classes.tagsBox}>
              {templateInfo?.tags.map((tag, index) => (
                <Tag
                  id={`ehandover-note-template-tags-del-${index}`}
                  tag={{ tag: tag.tagDesc }}
                  key={getArrayRandomKey(index, 'ehandover-note-template-tags-display')}
                  onRemove={() => handleDeleteTag(index)}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box className={classes.grid}>
          <Box
            id="ehandover-note-template-show-checklist-btn"
            className={classes.paper}
            flexGrow={1}
            onClick={() => {
              if (showCheckListBox) {
                setShowCheckListBox(false);
                return;
              }
              // 如果本来没有checkList，打开的时候自动插入一条初始化数据
              handleChangeTemplate('checkLists', [initCheckListItem]);
              setShowCheckListBox(true);
            }}
          >
            <ButtonBase>
              <CheckListIcon className={classes.icon} />
            </ButtonBase>
            <span className={classes.text}>{i18n.t('EHANDOVER_CREATENOTE_CHECKLIST')}</span>
          </Box>
          <Box className={classes.paper} flexGrow={1} />
          <Box className={classes.paper} flexGrow={1} />
        </Box>

        <Box className={classes.noteFooter}>
          <Button id="ehandover-note-template-cancel-btn" className={classes.cancelBtn} onClick={handleCancel}>
            {i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_CANCEL')}
          </Button>
          <Button id="ehandover-note-template-save-btn"  className={classes.confirmBtn} onClick={handleSave} disabled={disableSaveBtn}>
            {i18n.t('BEDSTATUS_SAVE')}
          </Button>
        </Box>
      </Box>
      <WarnAlert open={alertOpen} onClose={() => setAlertOpen(false)} text={i18n.t('EHANDOVER_CREATE_TASK_WARN')} />
    </Box>
  );
}
